import * as A from 'fp-ts/Array';
import { flow, pipe } from 'fp-ts/function';
import type { DocumentFragment, Element } from 'parse5';
import type { Decoration, Decoration_Type, Node } from 'ricos-schema';
import { RichContent } from 'ricos-schema';

import { createDecoration, initializeMetadata, reduceDecorations } from '../../../nodeUtils';
import type { ContentNode, Context, Rule } from './models';
import { getChildNodes, toAst } from './parse5-utils';

export type Interceptor = (context: {
  contentNode: ContentNode;
  context: Context;
  result: Node[];
}) => Node[];

const contextOf = (
  rules: Rule[],
  decorations: Decoration[],
  interceptorFn: Interceptor
): Context => ({
  visit: visit(rules, decorations, interceptorFn),
  decorations: reduceDecorations(decorations),
  addDecoration: addDecoration(rules, decorations, interceptorFn),
});

const htmlToNodes =
  (rules: Rule[], decorations: Decoration[], interceptorFn: Interceptor) =>
  (contentNode: ContentNode): Node[] => {
    const filteredRules = rules.filter(([_if]) => {
      try {
        return !!contentNode && _if(contentNode);
      } catch (e) {
        console.error(e, contentNode);
        return false;
      }
    });

    if (filteredRules.length === 0) {
      const context = contextOf(rules, decorations, interceptorFn);
      return interceptorFn({ contentNode, context, result: [] });
    }

    return filteredRules.flatMap(([, then]) => {
      const context = contextOf(rules, decorations, interceptorFn);
      const result = then(context)(contentNode);
      return interceptorFn({ contentNode, context, result });
    });
  };

const addDecoration =
  (rules: Rule[], decorations: Decoration[], interceptorFn: Interceptor) =>
  (type: Decoration_Type, data: Omit<Decoration, 'type'> = {}, element: Element) => {
    const decoration = createDecoration(type, data);
    return visit(rules, [decoration, ...decorations], interceptorFn)(element);
  };

const visit =
  (rules: Rule[], decorations: Decoration[], interceptorFn: Interceptor) =>
  (element: Element | DocumentFragment) =>
    pipe(element, getChildNodes, A.chain(htmlToNodes(rules, decorations, interceptorFn)));

const traverse = (rules: Rule[], interceptorFn: Interceptor) => visit(rules, [], interceptorFn);

const toRichContent = (nodes: Node[]): RichContent => ({ nodes, metadata: initializeMetadata() });

export default (rules: Rule[], interceptorFn: Interceptor) =>
  flow(toAst, traverse(rules, interceptorFn), toRichContent, RichContent.fromJSON);
